if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        // Easify temporarily changes the purchase control to type="button"
        // while it initializes. Find the stable named control so this custom
        // element cannot fail to upgrade during that window.
        this.submitButton = this.querySelector('[name="add"]') || this.querySelector('[type="submit"]');
        if (!this.form || !this.submitButton) return;

        if (this.variantIdInput) this.variantIdInput.disabled = false;
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.addEventListener('click', this.onSubmitButtonClick.bind(this), true);
        this.dataset.hhProductFormReady = 'true';

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitButtonClick(evt) {
        const currentButton = evt.target?.closest?.('[name="add"]');
        if (!currentButton || !this.contains(currentButton)) return;
        this.schedulePurchaseFallback(currentButton);
      }

      schedulePurchaseFallback(currentButton) {
        if (!currentButton || !this.contains(currentButton)) return;
        this.submitButton = currentButton;
        this.submitButtonText = currentButton.querySelector('span');
        window.clearTimeout(this.purchaseFallbackTimer);
        const cartCountBefore = document.querySelector('.cart-count-bubble')?.textContent || '';

        // Easify owns the first click while its option UI is active. If it does
        // not submit or begin loading, recover through the theme's canonical
        // product-form handler instead of leaving an enabled dead button.
        this.purchaseFallbackTimer = window.setTimeout(() => {
          const cartCountAfter = document.querySelector('.cart-count-bubble')?.textContent || '';
          if (
            this.submitButton.getAttribute('aria-disabled') === 'true' ||
            this.submitButton.disabled ||
            this.submitButton.classList.contains('loading') ||
            cartCountAfter !== cartCountBefore
          ) {
            return;
          }

          const formId = this.form.getAttribute('id');
          const colorInput = formId
            ? document.querySelector(
                `[name="properties[airtag-color-pattern]"][form="${CSS.escape(formId)}"]`
              )
            : null;
          if (
            colorInput &&
            (colorInput.offsetWidth > 0 || colorInput.offsetHeight > 0) &&
            !String(colorInput.value || '').trim()
          ) {
            this.handleErrorMessage('Please choose a Color / Pattern.');
            window.scrollTo({
              behavior: 'auto',
              top: Math.max(0, window.scrollY + colorInput.getBoundingClientRect().top - 140),
            });
            return;
          }

          if (!this.form.checkValidity()) {
            this.form.reportValidity();
            return;
          }

          this.onSubmitHandler(new Event('submit', { cancelable: true }));
        }, 1200);
      }

      onSubmitHandler(evt) {
        window.clearTimeout(this.purchaseFallbackTimer);
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
